<template>
    <div class="filters">
        <div v-for="filter of currentFilters" :key="filter.name" class="filter">
            <icon class="remove-filter" name="close-circle" v-if="!filter.locked" @click="removeFilter(filter)"/>
            <span class="filter-label">{{ filter.label }}</span>

            <template v-if="filter.type === 'text'">
                <text-filter :value="filter.value"
                             :disabled="filter.disabled"
                             @input="updateFilter(filter, $event)"
                />
            </template>
            <template v-if="filter.type === 'select' || filter.type === 'select-single'">
                <select-filter :value="filter.value"
                               :options="filter.options"
                               :disabled="filter.disabled"
                               :multiple="filter.type === 'select'"
                               @input="updateFilter(filter, $event)"
                />
            </template>
            <template v-if="filter.type === 'toggle'">
                <toggle-filter :value="filter.value"
                               :options="filter.options"
                               :disabled="filter.disabled"
                               @input="updateFilter(filter, $event)"
                />
            </template>
            <template v-if="filter.type === 'number'">
                <number-filter :value="filter.value"
                               :comp="filter.comp"
                               :disabled="filter.disabled"
                               @input="updateFilter(filter, $event)"
                />
            </template>
            <template v-if="filter.type === 'date'">
                <date-filter :value="filter.value"
                             :disabled="filter.disabled"
                             @input="updateFilter(filter, $event)"
                />
            </template>
        </div>

        <div v-if="filterOptions.length > 0" class="d-flex justify-content-between w-100">
            <btn theme="none" icon="plus" label="Filter hinzufügen" class="filter-button mr-3"/>
            <popover class="filter-popover" auto-bind ref="popover">
                <navigation>
                    <nav-item v-for="filter of filterOptions" :key="filter.name" :label="filter.label" @click="addFilter(filter)"/>
                </navigation>
            </popover>
            <btn theme="none"
                 class="filter-button mr-3"
                 icon="replay"
                 label="Filter zurücksetzen"
                 confirm="Soll die Filterung wirklich zurückgesetzt werden?"
                 @click="$emit('resetFilter')"
            />
            <btn theme="none"
                 icon="content-save"
                 label="Filter speichern"
                 class="filter-button mr-3"
                 modal="save-filter"
                 :payload="{type, filters: value}"
            />
            <btn theme="none"
                 icon="filter"
                 label="Filter laden"
                 class="filter-button"
                 modal="load-filter"
                 :payload="{type, filters: currentFilters}"
                 @loadFilter="handleLoadFilter"
            />
        </div>
    </div>
</template>

<style lang="scss">
    .filters {
        display: flex;
        flex-flow: row wrap;
        font-size: $font-size-sm;
        width: 100%;
        margin-bottom: -$spacer-1;
        margin-right: -$spacer-3;

        .filter {
            display: flex;
            align-items: center;
            margin-right: $spacer-3;
            margin-bottom: 15px;
            flex-basis: calc(33.333% - 25px);
            max-width: calc(50% - 25px);
        }
        .filter-label {
            margin-right: 10px;
            white-space: nowrap;
        }
        .remove-filter {
            font-size: 12px;
            color: $dark-30;
            cursor: pointer;
            margin-right: $spacer-1;

            &:hover {
                color: $dark-50;
            }
        }
        .filter-button {
            margin-left: auto;
            margin-right: auto;
            line-height: 35px;
        }

        .filter-popover .nav {
            max-height: 300px;
            overflow-y: auto;
        }
    }
</style>

<script>
    import uniq from 'lodash/uniq';

    export default {
        props: {
            value: Object,
            filters: Array,
            type: Number,
        },

        data() {
            let availableFilters = {};
            let configFilters = this.$config.get('filters', {});

            for (let filter of this.filters) {
                if (configFilters.hasOwnProperty(filter.name)) {
                    availableFilters[filter.name] = {...configFilters[filter.name], ...filter};
                }
            }

            return {
                availableFilters,
                addedFilters: Object.keys(this.value),
            };
        },

        computed: {
            lockedFilters() {
                let result = [];
                for (let filter of this.filters) {
                    if (filter.locked) {
                        result.push(filter.name);
                    }
                }
                return result;
            },

            usedFilterNames() {
                return uniq([...this.lockedFilters, ...this.addedFilters]);
            },

            currentFilters() {
                return this.usedFilterNames.map(name => {
                    let filter = this.availableFilters[name];
                    return {
                        ...filter,
                        ...this.mergeFilterValue(filter.name, filter.type),
                    };
                });
            },

            filterOptions() {
                let result = [];
                for (let {name} of this.filters) {
                    let filter = this.availableFilters[name];
                    if (filter && !this.usedFilterNames.includes(name)) {
                        result.push(filter);
                    }
                }
                return result;
            },
        },

        methods: {
            addFilter(filter) {
                this.addedFilters.push(filter.name);
                this.$refs.popover.close();
                this.$emit('addFilter', filter.name);
            },

            mergeFilterValue(name, type) {
                let oldValue = this.value[name] || {};
                let defaultValue = {value: null};

                if (type === 'number') {
                    defaultValue.comp = '>';
                } else if (type === 'select') {
                    defaultValue.value = [];
                }

                return {...defaultValue, ...oldValue};
            },

            removeFilter(filter) {
                let index =  this.addedFilters.indexOf(filter.name);
                if (~index) {
                    this.addedFilters.splice(index, 1);
                }
                if (this.value.hasOwnProperty(filter.name)) {
                    let valueCopy = {...this.value};
                    delete valueCopy[filter.name];
                    this.$emit('input', valueCopy);
                }
            },

            updateFilter(filter, {key, value}) {
                let valueCopy = {...this.value};

                if (!valueCopy.hasOwnProperty(filter.name)) {
                    valueCopy[filter.name] = this.mergeFilterValue(filter.name, filter.type);
                }

                if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
                    delete valueCopy[filter.name];
                } else {
                    valueCopy[filter.name][key] = value;
                }

                this.$emit('input', valueCopy);
            },

            handleLoadFilter(filters) {
                Object.keys(filters).forEach(e => {
                    if (!this.addedFilters.includes(e)) {
                        this.addedFilters.push(e);
                    }
                });
                this.$emit('loadFilter', filters);
            },

            resetAddedFilter() {
                this.addedFilters = Object.keys(this.value);
            },
        },
    };
</script>